import type { CardProps } from "@/modules/corporate/components/Card/Card"
import type { BlogArticle, BlogArticleSummary, BlogFilters } from "@/modules/corporate/types/blog"
import type { PagePagination } from "@/modules/corporate/types/pagePagination"
import type { Page } from "@/modules/corporate/types/pages"
import type { BlogSection } from "@/modules/corporate/types/sections"
import type { RichTextElement } from "@/modules/kontent/components/RichTextElement"
import type { ComponentProps, FunctionComponent } from "react"

import { clsx } from "clsx"
import dynamic from "next/dynamic"
import Image from "next/image"

import { Layout } from "@jobteaser/ui-kit/components/Layout/Layout"
import { Text, TEXT_COLORS, TEXT_TYPES, TEXT_WEIGHTS } from "@jobteaser/ui-kit/components/Text/Text"

import { InternalLink } from "@/modules/corporate/components/InternalLink"
import { DEFAULT_TIMEZONE } from "@/modules/dates/dates"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { RichText } from "@/modules/kontent/components/RichText"
import { updateRichTextHeadings } from "@/modules/kontent/helpers/updateRichTextHeadings"
import { getLocaleTerritoryCode } from "@/modules/locales/getLocaleTerritoryCode"
import { useLocale } from "@/modules/locales/useLocale"
import { useHash } from "@/modules/routing/hooks/useHash"

import { ArticleCategory } from "./ArticleCategory"
import { BlogCard } from "./BlogCard"
import { Image as CustomElementImage } from "./Image"

import styles from "./BlogArticle.module.css"

type BlogArticlePropsType = {
  article: BlogArticle
  currentPersonaRootPage: Page | null | undefined
  page: Page
  pagePagination: PagePagination<BlogArticleSummary, BlogFilters> | null
  section: BlogSection
}

type StrapiCustomElementProps =
  | {
      alt: string
      credit: string
      height: string
      src: string
      title: string
      variant: "image"
      width: string
    }
  | { variant?: never }

const getCustomTagComponents: () => ComponentProps<typeof RichTextElement>["tagComponents"] = () => ({
  // Strapi origin
  object: (props: StrapiCustomElementProps) => {
    if (props.variant === "image") {
      return (
        <CustomElementImage alt_description={props.alt} credit={props.credit} title={props.title} url={props.src} />
      )
    }

    return null
  },
})

const DynamicCard = dynamic<CardProps>(() => import("@/modules/corporate/components/Card/Card").then(mod => mod.Card))

export const BlogArticleComponent: FunctionComponent<BlogArticlePropsType> = ({
  article: { author, category, description, image, linkedArticle, parentPage, publicationDate, summary, title },
  currentPersonaRootPage,
  page,
  pagePagination,
  section,
}) => {
  const locale = useLocale()
  const hash = useHash()
  const { t } = useTranslation(["module_header_fo", "corporate_blog"])

  const shouldDisplayTableOfContents = section.showTableOfIndexes.includes("yes")
  const shouldDisplayLastArticles =
    (section.numberOfLastPublishedArticlesToShow || 0) > 0 && pagePagination?.items.length
  const shouldDisplayLinkedArticle = section.showLinkedArticle.includes("yes") && !!linkedArticle.length
  const shouldDisplayAsideColumn =
    shouldDisplayTableOfContents || shouldDisplayLastArticles || shouldDisplayLinkedArticle

  const [processedDescription, headingIds] = shouldDisplayTableOfContents
    ? updateRichTextHeadings(description)
    : [description, []]

  return (
    <>
      <Layout className={styles.wrapperHeader}>
        <Layout.Column>
          <Text className={styles.breadcrumb} resetSpacing type={TEXT_TYPES.SMALL} weight={TEXT_WEIGHTS.BOLD}>
            {currentPersonaRootPage ? (
              <Text className={styles.breadcrumbLink} page={currentPersonaRootPage} tag={InternalLink} />
            ) : (
              <Text className={styles.breadcrumbLink} href={`/${locale}`} tag="a">
                {t("module_header_fo.home")}
              </Text>
            )}
            <Text className={styles.breadcrumbLink} page={page} tag={InternalLink} />
            {parentPage.length ? (
              <Text
                className={styles.breadcrumbLink}
                page={{ slug: `${page.slug}/${parentPage[0].slug}`, title: parentPage[0].title }}
                tag={InternalLink}
              />
            ) : null}
            {title}
          </Text>
          <Text className={styles.title} tag="h1" type={TEXT_TYPES.HEADING_SMALL} weight={TEXT_WEIGHTS.BLACK}>
            {title}
          </Text>
          <ul className={styles.articleDetails}>
            {!!category.length && (
              <li className={styles.articleDetailsElement}>
                <ArticleCategory category={category[0].title} />
              </li>
            )}
            <Text className={styles.articleDetailsElement} color={TEXT_COLORS.GREY} tag="li">
              {new Intl.DateTimeFormat(getLocaleTerritoryCode(locale), {
                dateStyle: "full",
                timeZone: DEFAULT_TIMEZONE, // Default timezone is fine for article dates as they are not timezone-sensitive
              }).format(new Date(publicationDate))}
            </Text>
            <Text className={styles.articleDetailsElement} color={TEXT_COLORS.GREY} tag="li">
              {author[0].name}
            </Text>
          </ul>
          <Text type={TEXT_TYPES.SUBHEAD} weight={TEXT_WEIGHTS.BOLD}>
            {summary}
          </Text>
          <Image
            alt={image[0].description || ""}
            className={styles.image}
            height={image[0].height || undefined}
            priority
            src={image[0].url}
            width={image[0].width || undefined}
          />
        </Layout.Column>
      </Layout>
      <Layout hasBottomPadding isSmall={!shouldDisplayTableOfContents}>
        <Layout.Column>
          <RichText
            customComponents={{
              growth___corporate_page_section___card: DynamicCard,
            }}
            tagComponents={getCustomTagComponents()}
            value={processedDescription}
            wrapperProps={{
              className: styles.text,
              resetSpacing: true,
              tag: "div",
            }}
          />
        </Layout.Column>
        {shouldDisplayAsideColumn && (
          <Layout.Column as="aside" columns={5}>
            {shouldDisplayTableOfContents && !!headingIds.length && (
              <>
                <Text resetSpacing type={TEXT_TYPES.HEADING} weight={TEXT_WEIGHTS.BOLD}>
                  {t("corporate_blog.table_of_indexes")}
                </Text>
                <ul className={styles.indexesTable}>
                  {headingIds.map(({ id, name, tagName }) => (
                    <li
                      className={clsx(styles.indexesTableElement, {
                        [styles["indexesTableElement--current"]]: hash === id,
                      })}
                      data-level={tagName}
                      key={id}
                    >
                      <a className={styles.indexesTableLink} href={`#${id}`}>
                        {name}
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {shouldDisplayLastArticles && (
              <>
                <Text
                  className={styles.lastArticlesTitle}
                  resetSpacing
                  type={TEXT_TYPES.HEADING}
                  weight={TEXT_WEIGHTS.BOLD}
                >
                  {t("corporate_blog.last_published_articles")}
                </Text>
                <ul className={styles.lastArticlesList}>
                  {(pagePagination?.items || []).map(article => (
                    <li className={styles.lastArticlesListElement} key={article.title}>
                      <BlogCard article={article} page={page} withImage={false} />
                    </li>
                  ))}
                </ul>
              </>
            )}
            {shouldDisplayLinkedArticle && (
              <BlogCard article={linkedArticle[0]} className={styles.linkedArticle} page={page} />
            )}
          </Layout.Column>
        )}
      </Layout>
    </>
  )
}
